import invariant from 'tiny-invariant'
import { isProduction } from 'utils/env'
const GA4SiteTagId = process.env.NEXT_PUBLIC_GOOGLE_GA4_TAG || process.env.VITE_GOOGLE_GA4_TAG
const globalAdsTagId = process.env.NEXT_PUBLIC_GOOGLE_ADS_TAG || process.env.VITE_GOOGLE_ADS_TAG
const siteTagRegistrationEventId =
  process.env.NEXT_PUBLIC_GOOGLE_SITE_TAG_REGISTRATION_EVENT_ID ||
  process.env.VITE_GOOGLE_SITE_TAG_REGISTRATION_EVENT_ID

invariant(GA4SiteTagId, 'GOOGLE_GA4_TAG must be set inside GlobalSiteTag')

export enum GlobalSiteTagEventName {
  CONVERSION = 'conversion',
  VIEW_CART = 'view_cart',
  DOWNLOAD_PRODUCT = 'Clicked download - triggers file download',
  EXTENSION_12MONTHS_PURCHASED = '12month_extension_purchased',
  FREE_TRIAL_STARTED = 'free_trial_started',
  STUDIO_AI_FREE_TRIAL_STARTED = 'studio_ai_free_trial_started',
  MONTHLY_SUBCRIPTION_STARTED = 'monthly_subscription_started',
  YEARLY_SUBCRIPTION_STARTED = 'yearly_subscription_started',
  STUDIO_AI_YEARLY_SUBCRIPTION_STARTED = 'studio_ai_yearly_subscription_started',
  COIN_PACK_MONTHLY_25K_SUBCRIPTION_STARTED = 'coin_pack_monthly_25k_subscription_started',
  COIN_PACK_MONTHLY_100K_SUBCRIPTION_STARTED = 'coin_pack_monthly_100k_subscription_started',
  COIN_PACK_MONTHLY_250K_SUBCRIPTION_STARTED = 'coin_pack_monthly_250k_subscription_started',
  COIN_PACK_MONTHLY_400K_SUBCRIPTION_STARTED = 'coin_pack_monthly_400k_subscription_started',
  COIN_PACK_MONTHLY_700K_SUBCRIPTION_STARTED = 'coin_pack_monthly_700k_subscription_started',
  COIN_PACK_MONTHLY_1M_SUBCRIPTION_STARTED = 'coin_pack_monthly_1m_subscription_started',
  PAYMENT_MODAL_INTERACTION = 'payment_modal_interaction',
  //100 projects michaels
  PAGE_VIEWED = 'Page Viewed',
  CODE_SUBMITTED = 'Code Submitted',
  DOWNLOAD_INITIATED = 'Download Initiated',
  CODE_INVALID = 'Code Invalid',
  PRODUCTS_SECTION_VIEWED = 'Products Section Viewed',
  FAQ_SECTION_VIEWED = 'Faq Section Viewed',
  TRIAL_INITIATED = 'Trial Initiated',
  //Christmas in July
  CATEGORY_ITEM_CLICKED = 'Category Item Clicked',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GlobalSiteTagEventOptions = { [key: string]: any } | void
export type GlobalSiteTagEventFunction = (
  event: string,
  name: string,
  options: GlobalSiteTagEventOptions,
) => void

declare global {
  interface Window {
    gtag?: GlobalSiteTagEventFunction
  }
}

export const globalSiteSetUserId = (userId: string) => {
  if (typeof window.gtag === 'function') {
    window.gtag('config', GA4SiteTagId, {
      user_id: userId,
    })
  }
}

export const globalSiteInit = (userId: string | null) => {
  if (typeof window.gtag === 'function') {
    if (userId) {
      window.gtag('config', GA4SiteTagId, {
        user_id: userId,
      })
    } else {
      window.gtag('config', GA4SiteTagId)
    }
  }
}

export const globalSiteTagEvent = (
  name: GlobalSiteTagEventName,
  options: GlobalSiteTagEventOptions,
) => {
  if (!window.gtag) {
    return
  }

  window.gtag('event', name, options)
}

export const trackRegistrationConversion = () => {
  globalSiteTagEvent(GlobalSiteTagEventName.CONVERSION, {
    send_to: [`${globalAdsTagId}/${siteTagRegistrationEventId}`],
  })
}

export const trackViewCart = (productProps: GlobalSiteTagEventOptions) => {
  globalSiteTagEvent(GlobalSiteTagEventName.VIEW_CART, {
    send_to: `${GA4SiteTagId}/${siteTagRegistrationEventId}`,
    ...productProps,
  })
}

export const googleTrackProductDownloadClick = (eventLabel: string) => {
  globalSiteTagEvent(GlobalSiteTagEventName.DOWNLOAD_PRODUCT, {
    send_to: GA4SiteTagId,
    event_label: eventLabel,
    event_value: null,
    event_category: 'Prod Page',
  })
}

export function trackExtensionPurchased() {
  globalSiteTagEvent(GlobalSiteTagEventName.EXTENSION_12MONTHS_PURCHASED)
}

export function trackFreeTrialStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.FREE_TRIAL_STARTED)
}

export function trackStudioAiFreeTrialStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.STUDIO_AI_FREE_TRIAL_STARTED)
}

export function trackMonthlySubscriptionStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.MONTHLY_SUBCRIPTION_STARTED)
}

export function trackYearlySubscriptionStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.YEARLY_SUBCRIPTION_STARTED)
}

export function trackStudioAiYearlySubscriptionStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.STUDIO_AI_YEARLY_SUBCRIPTION_STARTED)
}

export function trackCoinPackMonthly25kSubscriptionStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.COIN_PACK_MONTHLY_25K_SUBCRIPTION_STARTED)
}

export function trackCoinPackMonthly100kSubscriptionStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.COIN_PACK_MONTHLY_100K_SUBCRIPTION_STARTED)
}

export function trackCoinPackMonthly250kSubscriptionStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.COIN_PACK_MONTHLY_250K_SUBCRIPTION_STARTED)
}

export function trackCoinPackMonthly400kSubscriptionStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.COIN_PACK_MONTHLY_400K_SUBCRIPTION_STARTED)
}

export function trackCoinPackMonthly700kSubscriptionStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.COIN_PACK_MONTHLY_700K_SUBCRIPTION_STARTED)
}

export function trackCoinPackMonthly1MSubscriptionStarted() {
  globalSiteTagEvent(GlobalSiteTagEventName.COIN_PACK_MONTHLY_1M_SUBCRIPTION_STARTED)
}

export const trackPaymentModalInteraction = () => {
  globalSiteTagEvent(GlobalSiteTagEventName.PAYMENT_MODAL_INTERACTION, {
    send_to: [`${globalAdsTagId}/${siteTagRegistrationEventId}`],
  })
}

export function initScript({ disableAds = false }: { disableAds?: boolean } = {}) {
  if (!disableAds && !globalAdsTagId) {
    throw new Error('GOOGLE_ADS_TAG must be set inside GlobalSiteTag')
  }

  return `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments)};
  gtag('js', new Date());
  
  ${isProduction && !disableAds ? `gtag('config', '${globalAdsTagId}');` : ''}`
}

export const scriptUrl = `https://www.googletagmanager.com/gtag/js?id=${GA4SiteTagId}`
